/**
 * mediaCompressor.js
 *
 * Browser-side image compression utility using the Canvas API.
 *
 * Key behaviours:
 *  - Images (JPEG, PNG, WebP, etc.): auto-compressed via canvas binary-search quality
 *    reduction followed by progressive resolution scaling if needed.
 *  - PNG/BMP/JFIF files are converted to JPEG for compression (white background fill
 *    prevents black artifacts). WebP stays WebP, JPEG stays JPEG.
 *  - Overload safe: Supports both (file, onProgress) and (file, maxSizeBytes, onProgress).
 *  - GIF files cannot be compressed via canvas — surfaced as FILE_TOO_LARGE.
 *  - PDFs / non-image files: returned as-is (caller is responsible for size check).
 *  - Zero external dependencies — pure browser Canvas API.
 *  - All paths wrapped in try/catch for maximum resilience.
 *
 * Exports:
 *   compressImageIfNeeded(file, maxSizeBytes?, onProgress?)      -> Promise<File>
 *   compressManyImagesIfNeeded(files, maxSizeBytes?, onProgress?) -> Promise<File[]>
 *   formatFileSize(bytes)                                         -> string
 *   isPDF(file)                                                   -> boolean
 *   isImage(file)                                                 -> boolean
 */

const DEFAULT_MAX_SIZE = 5 * 1024 * 1024; // 5 MB

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Format bytes into a human-readable string.
 * @example formatFileSize(3355443) => "3.2 MB"
 */
export function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(i >= 2 ? 1 : 0)} ${units[i]}`;
}

/**
 * Returns true if the given File is a PDF.
 */
export function isPDF(file) {
  if (!file) return false;
  const ext = file.name ? file.name.split('.').pop().toLowerCase() : '';
  return file.type === 'application/pdf' || ext === 'pdf';
}

/**
 * Returns true if the given File is an image (based on MIME type or extension).
 */
export function isImage(file) {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  const ext = file.name ? file.name.split('.').pop().toLowerCase() : '';
  const imageExtensions = ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'jfif', 'pjpeg', 'pjp', 'heic', 'heif', 'avif'];
  return Boolean(type.startsWith('image/') || imageExtensions.includes(ext));
}

// ─── Internal Canvas Helpers ────────────────────────────────────────────────

/**
 * Loads a File/Blob as an HTMLImageElement.
 * Revokes the object URL after load/error.
 */
function _loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for compression'));
    };
    img.src = url;
  });
}

/**
 * Renders an image onto an offscreen canvas and returns a Blob.
 * White background fill prevents black artifacts on PNG->JPEG conversion.
 */
function _renderToBlob(img, width, height, type, quality) {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas 2D context not available in this browser'));
        return;
      }
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('canvas.toBlob returned null'));
        },
        type,
        quality
      );
    } catch (err) {
      reject(err);
    }
  });
}

// ─── Main API ────────────────────────────────────────────────────────────────

/**
 * Compresses an image file to fit within maxSizeBytes.
 *
 * Supports both signatures:
 *   compressImageIfNeeded(file, onProgress)
 *   compressImageIfNeeded(file, maxSizeBytes, onProgress)
 *
 * Phase 1: Binary-search quality reduction at original resolution (10 iterations).
 * Phase 2: Progressive resolution scaling (85%->70%->55%->40%->30%->20%->15%) with adaptive quality levels.
 * Phase 3: Failsafe progressive resolution for extreme image sizes.
 *
 * @param {File} file
 * @param {number|Function} [maxSizeBytes=5242880]  5 MB default, or onProgress callback
 * @param {Function} [onProgress=null]              Progress callback (percent, message)
 * @returns {Promise<File>} The compressed (or original if already small) file.
 * @throws {Error} 'FILE_TOO_LARGE' if unable to compress to target size.
 */
export async function compressImageIfNeeded(file, maxSizeBytes = DEFAULT_MAX_SIZE, onProgress = null) {
  // Support overload: compressImageIfNeeded(file, onProgress)
  if (typeof maxSizeBytes === 'function') {
    onProgress = maxSizeBytes;
    maxSizeBytes = DEFAULT_MAX_SIZE;
  }
  if (!maxSizeBytes || typeof maxSizeBytes !== 'number' || maxSizeBytes <= 0) {
    maxSizeBytes = DEFAULT_MAX_SIZE;
  }

  // Non-image (PDF, Excel, etc.) - return as-is; caller handles size validation
  if (!isImage(file)) return file;

  // Already within limit - no compression needed
  if (file.size <= maxSizeBytes) return file;

  const originalSize = file.size;

  // GIF cannot be meaningfully compressed via canvas (loses animation)
  if (file.type === 'image/gif') {
    console.error('[MediaCompressor] GIF too large - cannot compress:', {
      name: file.name,
      size: formatFileSize(file.size),
    });
    throw new Error('FILE_TOO_LARGE');
  }

  onProgress?.(15, 'Reading image file...');

  const ext = file.name ? file.name.split('.').pop().toLowerCase() : '';
  let outputType = 'image/jpeg';
  if (file.type === 'image/webp' || ext === 'webp') {
    outputType = 'image/webp';
  }

  let img;
  try {
    img = await _loadImage(file);
  } catch (err) {
    console.error('[MediaCompressor] Failed to load image:', { name: file.name, error: err.message });
    throw err;
  }

  onProgress?.(30, 'Optimizing image quality...');

  let origWidth = img.naturalWidth || img.width || 1920;
  let origHeight = img.naturalHeight || img.height || 1080;

  // Real-world performance guard: If image is gigantic (e.g. 6000x4000 24MP DSLR / iPhone RAW),
  // clamp initial dimensions to max 2560px for ultra-sharp 2K/4K presentation while keeping canvas fast.
  const MAX_INITIAL_DIM = 2560;
  if (origWidth > MAX_INITIAL_DIM || origHeight > MAX_INITIAL_DIM) {
    const initialScale = Math.min(MAX_INITIAL_DIM / origWidth, MAX_INITIAL_DIM / origHeight);
    origWidth = Math.max(1, Math.round(origWidth * initialScale));
    origHeight = Math.max(1, Math.round(origHeight * initialScale));
  }

  // Phase 1: Binary-search quality at original dimensions (or clamped 2560px)
  let lo = 0.1;
  let hi = 0.92;
  let bestBlob = null;

  for (let i = 0; i < 10; i++) {
    const mid = (lo + hi) / 2;
    const currentProgress = 30 + Math.round(((i + 1) / 10) * 40);
    onProgress?.(currentProgress, `Optimizing quality (${currentProgress}%)...`);
    try {
      const blob = await _renderToBlob(img, origWidth, origHeight, outputType, mid);
      if (blob && blob.size <= maxSizeBytes) {
        bestBlob = blob;
        lo = mid; // Fits - try higher quality
      } else {
        hi = mid; // Too large - try lower quality
      }
    } catch (err) {
      console.error('[MediaCompressor] Canvas render failed during quality search:', err.message);
      break;
    }
  }

function _formatCompressedFilename(originalName, outputType) {
  if (!originalName) return outputType === 'image/webp' ? 'image.webp' : 'image.jpg';
  const lastDot = originalName.lastIndexOf('.');
  const baseName = lastDot !== -1 ? originalName.substring(0, lastDot) : originalName;
  const currentExt = lastDot !== -1 ? originalName.substring(lastDot + 1).toLowerCase() : '';
  
  if (outputType === 'image/jpeg') {
    if (currentExt === 'jpg' || currentExt === 'jpeg') return originalName;
    return `${baseName}.jpg`;
  }
  if (outputType === 'image/webp') {
    if (currentExt === 'webp') return originalName;
    return `${baseName}.webp`;
  }
  return originalName;
}

  if (bestBlob && bestBlob.size <= maxSizeBytes) {
    onProgress?.(100, 'Image optimized successfully!');
    const finalFilename = _formatCompressedFilename(file.name, outputType);
    const result = new File([bestBlob], finalFilename, {
      type: outputType,
      lastModified: Date.now(),
    });
    console.warn('[MediaCompressor] Image compressed (quality reduction):', {
      name: finalFilename,
      originalSize: formatFileSize(originalSize),
      compressedSize: formatFileSize(result.size),
    });
    return result;
  }

  // Phase 2: Progressive resolution reduction with adaptive quality search
  const scales = [0.85, 0.70, 0.55, 0.40, 0.30, 0.20, 0.15];
  const testQualities = [0.85, 0.70, 0.50, 0.30, 0.15];
  for (let scaleIdx = 0; scaleIdx < scales.length; scaleIdx++) {
    const scale = scales[scaleIdx];
    const currentProgress = 70 + Math.round(((scaleIdx + 1) / scales.length) * 25);
    onProgress?.(currentProgress, `Optimizing resolution and detail (${currentProgress}%)...`);
    const w = Math.max(1, Math.floor(origWidth * scale));
    const h = Math.max(1, Math.floor(origHeight * scale));

    for (const q of testQualities) {
      try {
        const blob = await _renderToBlob(img, w, h, outputType, q);
        if (blob && blob.size <= maxSizeBytes) {
          onProgress?.(100, 'Image optimized successfully!');
          const finalFilename = _formatCompressedFilename(file.name, outputType);
          const result = new File([blob], finalFilename, {
            type: outputType,
            lastModified: Date.now(),
          });
          console.warn('[MediaCompressor] Image compressed (adaptive scale + quality):', {
            name: finalFilename,
            originalSize: formatFileSize(originalSize),
            compressedSize: formatFileSize(result.size),
            scale: `${Math.round(scale * 100)}%`,
            quality: q,
          });
          return result;
        }
      } catch (err) {
        console.error('[MediaCompressor] Canvas render failed during resolution reduction:', err.message);
      }
    }
  }

  // Phase 3: Failsafe fallback
  try {
    const fallbackW = Math.max(1, Math.floor(origWidth * 0.10));
    const fallbackH = Math.max(1, Math.floor(origHeight * 0.10));
    const fallbackBlob = await _renderToBlob(img, fallbackW, fallbackH, outputType, 0.10);
    if (fallbackBlob && fallbackBlob.size <= maxSizeBytes) {
      onProgress?.(100, 'Image optimized successfully!');
      const finalFilename = _formatCompressedFilename(file.name, outputType);
      return new File([fallbackBlob], finalFilename, {
        type: outputType,
        lastModified: Date.now(),
      });
    }
  } catch (err) {
    console.error('[MediaCompressor] Fallback compression failed:', err);
  }

  // Cannot reach target size even after maximum compression
  console.error('[MediaCompressor] File still too large after max compression:', {
    name: file.name,
    originalSize: formatFileSize(originalSize),
  });
  throw new Error('FILE_TOO_LARGE');
}

/**
 * Batch-compress an array of files sequentially (avoids memory pressure).
 * Throws on the first file that cannot be compressed.
 *
 * Supports both signatures:
 *   compressManyImagesIfNeeded(files, onProgress)
 *   compressManyImagesIfNeeded(files, maxSizeBytes, onProgress)
 *
 * @param {File[]} files
 * @param {number|Function} [maxSizeBytes=5242880]
 * @param {Function} [onProgress=null] Callback: (percent: number, message: string) => void
 * @returns {Promise<File[]>}
 */
export async function compressManyImagesIfNeeded(files, maxSizeBytes = DEFAULT_MAX_SIZE, onProgress = null) {
  if (typeof maxSizeBytes === 'function') {
    onProgress = maxSizeBytes;
    maxSizeBytes = DEFAULT_MAX_SIZE;
  }
  if (!maxSizeBytes || typeof maxSizeBytes !== 'number' || maxSizeBytes <= 0) {
    maxSizeBytes = DEFAULT_MAX_SIZE;
  }

  const results = [];
  const total = files.length;
  for (let idx = 0; idx < total; idx++) {
    const file = files[idx];
    const compressed = await compressImageIfNeeded(file, maxSizeBytes, (filePercent, fileMsg) => {
      const overall = Math.min(99, Math.round((idx / total) * 100 + (filePercent / total)));
      const msg = total > 1 ? `Optimizing image ${idx + 1} of ${total} (${overall}%)...` : fileMsg;
      onProgress?.(overall, msg);
    });
    results.push(compressed);
  }
  onProgress?.(100, 'All images processed!');
  return results;
}
